import { SelectInput, SelectInputOptions } from '@/components/ui/custom/select-input'
import { PORTONE_PAY_METHOD, TPortOnePayMethod } from '@/lib/PortOne/portOne.constants'
import { INFO_URL } from '@/service/info'
import { useInfo } from '@/service/queries/useInfo'

export interface IPaymentMethodValues {
  payMethod: TPortOnePayMethod
  refundHolder: string
  refundBankCode: string
  refundAccount: string
}

type TRefundField = 'refundHolder' | 'refundBankCode' | 'refundAccount'

export default function ProductPaymentMethodFields({
  value,
  onPayMethodChange,
  onRefundChange,
}: {
  value: IPaymentMethodValues
  onPayMethodChange: (method: TPortOnePayMethod) => void
  onRefundChange: (field: TRefundField, value: string) => void
}) {
  const { data: bankList, isLoading, isError } = useInfo<{ id: number; name: string }>({
    url: INFO_URL.BANK_CODE,
    enabled: value.payMethod === PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT,
  })
  const bankOptions = bankList?.map(({ id, name }) => ({ id, value: name })) ?? []

  return (
    <div className="mt-6 border-t border-gray-100 pt-5">
      <fieldset className="space-y-3">
        <legend className="text-sm font-bold">결제 수단</legend>
        <div className="flex gap-5 text-sm">
          {(
            [
              [PORTONE_PAY_METHOD.CARD, '카드'],
              [PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT, '가상계좌'],
            ] as const
          ).map(([method, label]) => (
            <label key={method} className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                checked={value.payMethod === method}
                onChange={() => onPayMethodChange(method)}
                className="accent-gray-900"
              />
              {label}
            </label>
          ))}
        </div>
        {value.payMethod === PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT && (
          <div className="grid gap-2 text-sm">
            <p className="text-xs text-gray-500">환불받을 계좌를 입력해주세요.</p>
            <input
              type="text"
              aria-label="환불 계좌 예금주"
              placeholder="예금주"
              value={value.refundHolder}
              onChange={e => onRefundChange('refundHolder', e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
            />
            <SelectInput
              value={bankOptions.find(bank => bank.value === value.refundBankCode) ?? null}
              label={value.refundBankCode}
              placeholder={
                isLoading
                  ? '은행 목록 불러오는 중...'
                  : isError
                    ? '은행 목록을 불러오지 못했습니다.'
                    : bankOptions.length
                      ? '환불 은행 선택'
                      : '선택 가능한 은행이 없습니다.'
              }
              disabled={isLoading || isError || !bankOptions.length}
              onChange={({ value: bankName }) => onRefundChange('refundBankCode', bankName)}
              className="!min-h-[38px] !rounded-lg !border-gray-200 !px-3 !py-0 !text-sm sm:!min-w-0 md:!px-3 md:!py-0"
            >
              <SelectInputOptions options={bankOptions} />
            </SelectInput>
            <input
              type="text"
              inputMode="numeric"
              aria-label="환불 계좌번호"
              placeholder="계좌번호"
              value={value.refundAccount}
              onChange={e => onRefundChange('refundAccount', e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
            />
          </div>
        )}
      </fieldset>
    </div>
  )
}
